<template>
  <div v-if="venueCampaign">
    <vx-card no-shadow>
      <div class="flex">
        <div class="flex-grow flex-shrink-1 pr-4">
          <h2 class="mt-4">Sms</h2>
          <h5 class="my-5" v-if="!isEditable">
            <b>Note: Cannot be saved as campaign has status {{ statusObject(venueCampaign.status).text }}.</b>
          </h5>

          <div class="col-8 mb-5">
            <div class="vx-row mb-6">
              <div class="vx-col sm:w-4/5 max-w-lg">
                <p class="my-2">
                  This field is for your SMS campaign. Because there is a limit of only 160 characters per SMS
                  you cannot use emojis. Please remember to include the <i>unsub phrase</i> and preview your
                  SMS campaign to make sure potential merge tags don't make your campaign exceed the character
                  limit.<br />
                  SMS price list (in danish kroner), extracted from <a href="https://www.inmobile.com/da/priser/">InMobile pricing list</a>:
                  <ul class="ml-4">
                    <li><b>Danish number:</b> 0.259 DKK per sms</li>
                    <li><b>Norwegian number:</b> 0.290 DKK per sms</li>
                    <li><b>Finnish number:</b> 0.320 DKK per sms</li>
                    <li><b>English number:</b> 1.490 DKK per sms</li>
                  </ul>

                </p>

                <vs-textarea
                  label=""
                  v-model="venueCampaign.smsContent"
                  height="200px"
                  class="smsContentContainer"
                  ref="textarea"
                  counter="120"
                />
                <vs-chip
                  class="cursor-pointer"
                  @click.native="addHandlebar(item.handlebar)"
                  v-for="item in handlebars"
                  :key="item.handlebar"
                >
                  <b>{{ item.name }}</b>
                </vs-chip>
              </div>
            </div>
            <vs-row> </vs-row>
          </div>
        </div>
        <div class="flex justify-end flex-shrink-0">
          <div class="mt-8">
            <vx-card class="max-w-md">
              <h5 class="mb-1">Checklist</h5>
              <div class="mb-3 pr-3">The sms must contain the following:</div>

              <div class="ml-2">
                <vs-row>
                  <feather-icon
                    :icon="requiredTags.unsub_phrase ? 'CheckSquareIcon' : 'SquareIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span class="font-bold" :class="{ 'line-through': requiredTags.unsub_phrase }"
                    >Unsub phrase</span
                  >
                </vs-row>
              </div>
              <vs-progress :height="8" :percent="requiredTags.progress" class="mt-2" color="#24c1a0"
                >success</vs-progress
              >

              <vs-divider />

              <h5>Message info</h5>
              <div class="mb-3 pr-3">
                The following is an estimate. <br />
                Actual size will vary based on merge tags:
              </div>
              <div>
                <div class="flex items-center">
                  <feather-icon
                    :icon="numSms < 2 ? 'CheckSquareIcon' : 'AlertCircleIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span><b>Character count:</b> {{ smsLength }}</span>
                </div>
                <div class="flex items-center">
                  <feather-icon
                    :icon="numSms < 2 ? 'CheckSquareIcon' : 'AlertCircleIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span><b>Message count:</b> {{ numSms }} per guest</span>
                </div>
                <div class="flex items-center">
                  <feather-icon
                  icon="DollarSignIcon"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <div>
                    <div><b>Price estimate per guest:</b></div>
                    <ul class="ml-2">
                      <li><b>Danish number:</b> {{ (numSms * 0.259).toFixed(3) }} DKK</li>
                      <li><b>Norwegian number:</b> {{ (numSms * 0.29).toFixed(3) }} DKK</li>
                      <li><b>Finnish number:</b> {{ (numSms * 0.32).toFixed(3) }} DKK</li>
                      <li><b>English number:</b> {{ (numSms * 1.49).toFixed(3) }} DKK</li>
                    </ul>
                  </div>
                </div>
              </div>
            </vx-card>
          </div>
        </div>
      </div>
      <!-- Save & Remove Button -->
      <vs-row
        vs-justify="flex-end"
        class="flex flex-wrap items-center"
        style="margin-top: 32px"
        v-if="hasPermissionForActiveVenue('vp.vp.venuecampaigns.edit')"
      >
        <vs-button v-if="isEditable" @click="onClickSendTest" class="mt-2">Send test</vs-button>
        <vs-button v-if="isEditable" id="save-button" @click="$emit('onSave')" class="mt-2 ml-4"
          >Save Changes</vs-button
        >
      </vs-row>
    </vx-card>

    <vx-card no-shadow> </vx-card>
  </div>
</template>

<script>
import _ from "lodash";
import campaignMixin from "@/views/mixins/campaignMixin";

export default {
  props: ["venueCampaign", "isEditable"],
  mixins: [campaignMixin],
  computed: {
    smsLength() {
      return this.venueCampaign.smsContent.length;
    },
    numSms() {
      return Math.floor(this.smsLength / 120) + 1;
    },
    handlebars() {
      return this.$store.getters["handlebars/getFor"]("guest", "venue", "sendDate")
        .filter(e => e.handlebar != "venue_openhours" && e.handlebar != "venue_openhours_html")
        .map(e => {
          return {
            handlebar: `{{${e.handlebar}}}`,
            name: e.name,
            description: e.description,
          };
        });
    },
    requiredTags() {
      const result = {
        unsub_phrase: this.venueCampaign.smsContent.includes("{{venue_sms_unsub_phrase}}"),
      };

      return {
        ...result,
        progress: (_.chain(result).values().sum().value() / _.values(result).length) * 100,
        isValid: _.chain(result).values().every().value(),
      };
    },
  },
  methods: {
    addHandlebar(handlebar) {
      console.log(this.venueCampaign.smsContent);
      // this.venueCampaign.smsContent += handlebar;

      const el = this.$refs.textarea.$el.children[0];
      const [start, end] = [el.selectionStart, el.selectionEnd];
      el.setRangeText(handlebar, start, end, "select");
      this.venueCampaign.smsContent = el.value;
    },

    async onClickSendTest() {
      this.$emit("onSave");

      try {
        this.$vs.loading();
        await this.$store.dispatch("venueCampaigns/sendTestSms", {
          venueCampaignId: this.venueCampaign.id,
        });
        this.$vs.notify({
          color: "green",
          title: "Test sms sent",
          text: "A test sms has been sent to your phone",
        });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Error sending test sms",
          text: err,
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>


<style lang="scss" scoped>
.smsContentContainer textarea {
  height: 100%;
}
</style>